<i18n src="./resources/locales.json"></i18n>

<template>
  <div id="agentInformationCenter" v-intersect="(entries, observer) => onIntersect(entries, observer)">
    <div
      v-show="preferencesSettings.pauseKPIEvents && !hideLiveUpdatesMessage"
      class="paused-kpi-events elevation-2"
      :dark="$vuetify.theme.dark"
    >
      <v-icon small class="alert-icon">mdi-alert-circle</v-icon> {{ $t('liveKPIsPaused.title') }}
      <div class="re-enable">
        You can re-enable them <a @click="enableLiveUpdates">here</a> or through agent preferences.
      </div>
      <div class="message">{{ $t('liveKPIsPaused.message') }}</div>
      <v-btn class="close-btn" icon small @click="hideLiveUpdatesMessage = !hideLiveUpdatesMessage">
        <v-icon small>mdi-close</v-icon>
      </v-btn>
    </div>
    <div class="grid">
      <div v-for="(card, index) in dashboard.cards" :id="card._id || card.componentName" :key="index" class="item">
        <component
          :is="card.componentName"
          :key="card._id || index"
          :only-for-business-process-id="card.selectedBusinessProcessId"
          :start-business-process-id="card.startBusinessProcessId || ''"
          :disabled="isLoading"
          class="item-content"
          @saveCardStartingBusinessProcess="data => onSaveCardStartingBusinessProcess(index, data)"
          @resize="onResize"
        />
      </div>
    </div>

    <v-tooltip v-if="selectedDashboard" color="secondary" left>
      <template v-slot:activator="{ on }">
        <v-btn class="mx-2 aic-fab" color="accent" fab dark @click="openAicConfig" v-on="on">
          <v-icon>mdi-view-dashboard</v-icon>
        </v-btn>
      </template>
      <span v-once>
        {{ $t('dashboardSetup') }}
      </span>
    </v-tooltip>
    <EcsAicConfigDialog ref="aicConfigDialog" />
  </div>
</template>

<script>
import Muuri from 'muuri';
import { mapActions, mapGetters } from 'vuex';
import { debounce } from 'lodash';
import EcsAgentMetricsCard from '@/components/ecs-agent-metrics-card/EcsAgentMetricsCard.vue';
import EcsAgentsSummaryCard from '@/components/ecs-agents-summary-card/EcsAgentsSummaryCard';
import EcsQueueStatusCard from '@/components/ecs-queue-status-card/EcsQueueStatusCard';
import EcsAicConfigDialog from '@/components/ecs-aic-config-dialog/EcsAicConfigDialog';
import EcsAgentKPICard from '@/components/ecs-agent-kpi-card/EcsAgentKPICard';
import EcsQueueSummaryCard from '@/components/ecs-queue-summary-card/EcsQueueSummaryCard';
import EcsQueueBusinessProcessSummaryCard from '@/components/ecs-queue-business-process-summary-card/EcsQueueBusinessProcessSummaryCard';

export default {
  name: 'EcsAgentInformationCenter',

  components: {
    EcsAgentKPICard,
    EcsAgentMetricsCard,
    EcsAgentsSummaryCard,
    EcsAicConfigDialog,
    EcsQueueStatusCard,
    EcsQueueSummaryCard,
    EcsQueueBusinessProcessSummaryCard
  },

  data() {
    return {
      resizeOb: null,
      currentWidth: 0,
      isLoading: false,
      currentCardsOrder: [],
      hideLiveUpdatesMessage: false
    };
  },

  computed: {
    ...mapGetters('DASHBOARD', ['selectedDashboard']),

    ...mapGetters('SETTINGS', ['dashboardSettings', 'preferencesSettings']),

    dashboard() {
      return this.selectedDashboard || { cards: [] };
    }
  },

  watch: {
    selectedDashboard() {
      this.initializeMuuri();
    }
  },

  async mounted() {
    this.resizeOb = new ResizeObserver(this.reinitilizeMuuriOnWidthResize);
    this.resizeOb.observe(this.$el);

    if (this.dashboardSettings.dashboards.length === 0) {
      await this.createCurrentUserDefaultDashboard();
    }

    await this.$nextTick();
    this.setSelectedDashboard(this.dashboardSettings.dashboards[0]);

    await this.$nextTick();
    this.initializeMuuri();

    this.$root.$on('edit-selected-dashboard', this.openAicConfig);
  },

  beforeDestroy() {
    if (this.$root.agentInformationCenterGrid) {
      this.$root.agentInformationCenterGrid.off('dragEnd', this.storeCardsOrder);
      this.$root.agentInformationCenterGrid.destroy(true);
    }

    this.$root.$off(`edit-selected-dashboard`, this.openAicConfig);
    this.resizeOb.unobserve(this.$el);
  },

  methods: {
    ...mapActions('DASHBOARD', ['setSelectedDashboard']),

    ...mapActions('STATS', ['updateAgentsStats', 'updateBusinessProcessStats']),

    ...mapActions('SETTINGS', [
      'updatePauseKPIEvents',
      'updateCurrentUserDashboardSettings',
      'createCurrentUserDefaultDashboard',
      'updateCurrentUserDashboardCardBusinessProcess'
    ]),

    updateStats() {
      if (this.$router.currentRoute.name === 'Dashboards') {
        this.updateBusinessProcessStats();
        this.updateAgentsStats();
      } else {
        clearInterval(this.$root.updateStatsCycleRef);
      }
    },

    onIntersect(entries) {
      if (entries[0] && entries[0].isIntersecting) {
        if (this.$root.updateStatsCycleRef) {
          clearInterval(this.$root.updateStatsCycleRef);
        }
        this.updateStats();
        this.$root.updateStatsCycleRef = setInterval(this.updateStats, 10 * 60 * 1000);
      }
    },

    refreshLayout: debounce(async function() {
      await this.$nextTick();
      if (this.$root.agentInformationCenterGrid) {
        this.$root.agentInformationCenterGrid.refreshItems().layout();
      }
    }, 100),

    initializeMuuri: debounce(async function() {
      if (!this.dashboard.cards || !this.dashboard.cards.length) {
        return;
      }

      await this.$nextTick();
      try {
        if (this.$root.agentInformationCenterGrid) {
          this.$root.agentInformationCenterGrid.destroy();
          delete this.$root.agentInformationCenterGrid;
        }

        this.$root.agentInformationCenterGrid = new Muuri('.grid', {
          dragEnabled: true,
          dragStartPredicate: (item, event) => {
            if (this.isLoading) {
              return false;
            }
            return Muuri.ItemDrag.defaultStartPredicate(item, event);
          }
        });
        this.$root.agentInformationCenterGrid.on('dragEnd', this.storeCardsOrder);
        this.currentCardsOrder = this.getMuuriItemsOrder();
      } catch {}
    }, 200),

    openAicConfig() {
      this.$refs.aicConfigDialog
        .open()
        .then(this.initializeMuuri)
        .catch(this.refreshLayout);
    },

    onResize() {
      setTimeout(this.refreshLayout, 250);
    },

    getMuuriItemsOrder() {
      return this.$root.agentInformationCenterGrid.getItems().map(item => item.getElement().getAttribute('id'));
    },

    storeCardsOrder() {
      const orderedCardsIds = this.getMuuriItemsOrder();
      if (this.currentCardsOrder.join() === orderedCardsIds.join()) {
        return;
      }

      this.currentCardsOrder = orderedCardsIds;
      const dashboards = this.dashboardSettings.dashboards;

      const updatedDashboards = dashboards.map(dashboard => {
        if (this.dashboard._id === dashboard._id) {
          const sortedCards = [];
          orderedCardsIds.forEach(id => {
            sortedCards.push(dashboard.cards.find(card => card._id === id));
          });
          dashboard.cards = sortedCards;
        }
        return dashboard;
      });

      this.isLoading = true;
      this.updateCurrentUserDashboardSettings({ dashboards: updatedDashboards }).finally(() => {
        this.isLoading = false;
      });
    },

    onSaveCardStartingBusinessProcess(index, bpId) {
      this.isLoading = true;
      const dashboardIndex = this.dashboardSettings.dashboards.findIndex(elem => elem._id === this.dashboard._id);
      this.updateCurrentUserDashboardCardBusinessProcess({ dashboardIndex, cardIndex: index, bpId }).finally(() => {
        this.isLoading = false;
      });
    },

    enableLiveUpdates() {
      this.updatePauseKPIEvents(this.preferencesSettings.pauseKPIEvents);
    },

    reinitilizeMuuriOnWidthResize: debounce(async function(entries) {
      const width = entries[0].contentRect.width;

      if (this.currentWidth !== width) {
        this.currentWidth = width;
        await this.$nextTick();
        this.initializeMuuri();
      }
    }, 100)
  }
};
</script>

<style src="./scoped.css" scoped />
